import { frameId, type CompileContext, type MapDocument, type PropBehaviour, type PropInstance, type TileRef, type TransitionStrip, type ValidationIssue, type WorldDocument } from "daedalus-core";
import { WELL } from "./type-names.ts";

/**
 * Carries a well saved with two states over to the three-state well (ADR-042's amendment of 2026-10-03,
 * P1.6-T79). It is pure, so every reader of a document applies it in memory: the compile, so a world not yet
 * saved in the new shape still compiles and publishes with its animation; the editor, as it opens a world, so
 * the first save writes the new shape; and `world:migrate`, which writes it. A two-state well was placed on its raised frame and named a `lowered` one; the well now stands
 * lowered on its placed frame and names a `raised` and a `raisedEmpty` frame, with the strips its bucket
 * plays. Everything is read from the document and the world's one-shot strips, never from tile numbers:
 *
 * - the well stands on its old lowered frame;
 * - `raised` rests on the last frame of the strip the old lowered frame starts, named `water`, or on the old
 *   placed frame when that frame starts none;
 * - `raisedEmpty` rests on the last frame of the strip the old placed frame starts, named `empty`, or where
 *   `raised` rests when it starts none, or the same strip as the water's.
 *
 * On Aethervale's well (placed on the empty-bucket strip's first frame, lowered on the water-bucket strip's)
 * that names both strips and rests each raised state on its strip's last frame. On a well of still frames
 * it swaps the two frames and names nothing, so the well cuts as it did. A well that already names a
 * `raised` frame, or that never named a `lowered` one, is left as it is, so a second run changes nothing. A
 * well placed the other way round (on the water strip's first frame, 669, lowered on the empty strip's, 639)
 * is carried over by the same rule and so names the strips the other way: `water` plays the empty-bucket strip
 * and raising brings up an empty bucket. The two frames draw alike, so the document cannot tell; the line
 * printed for each well names the frames, and the chooser swaps the two strips.
 */

/**
 * One carried well, for the report: where it is and the frame each state now rests on, with the animation
 * names it was given a strip for.
 */
export interface WellReport {
  readonly mapId: string;
  readonly layerId: string;
  readonly propId: string;
  readonly lowered: TileRef;
  readonly raised: TileRef;
  readonly raisedEmpty: TileRef;
  readonly animations: readonly string[];
}

/**
 * The strip a frame starts and the frame it ends on.
 *
 * @param strips The world's one-shot strips by the frame id of their first frame.
 * @param frame The frame.
 * @returns The strip's last frame, or undefined when the frame starts no strip.
 */
function stripEnd(strips: Readonly<Record<string, TransitionStrip>>, frame: TileRef): TileRef | undefined {
  const last = strips[frameId(frame.tileset, null, frame.tile)]?.frames.at(-1);
  return last === undefined ? undefined : { tileset: last.tileset, tile: last.tile };
}

/**
 * Whether two frames are the same tile of the same tileset.
 *
 * @param a One frame.
 * @param b The other.
 * @returns True when they are.
 */
function sameFrame(a: TileRef, b: TileRef): boolean {
  return a.tileset === b.tileset && a.tile === b.tile;
}

/**
 * One prop carried over, or the prop itself when it is not a two-state well.
 *
 * @param prop The placed prop.
 * @param strips The world's one-shot strips by the frame id of their first frame.
 * @param frameWidth Frame width in pixels, undefined when the frame has no known size.
 * @param tileSize Tile size in pixels.
 * @returns The prop and, when it was carried over, what each state rests on.
 */
function carried(prop: PropInstance, strips: Readonly<Record<string, TransitionStrip>>, frameWidth: (frame: TileRef) => number | undefined, tileSize: number): { readonly prop: PropInstance; readonly report?: Omit<WellReport, "mapId" | "layerId"> } {
  const behaviour = prop.behaviour;
  const lowered = behaviour?.frames?.["lowered"];
  if (behaviour === undefined || behaviour.type !== WELL || lowered === undefined || behaviour.frames?.["raised"] !== undefined) {
    return { prop };
  }
  const placed = prop.frame;
  const waterEnd = stripEnd(strips, lowered);
  const emptyEnd = sameFrame(placed, lowered) ? undefined : stripEnd(strips, placed);
  const raised = waterEnd ?? placed;
  const raisedEmpty = emptyEnd ?? raised;
  const { lowered: _lowered, ...otherFrames } = behaviour.frames ?? {};
  const named: Record<string, TileRef> = { ...(waterEnd === undefined ? {} : { water: lowered }), ...(emptyEnd === undefined ? {} : { empty: placed }) };
  const animations = { ...behaviour.animations, ...named };
  const { frames: _frames, animations: _animations, ...rest } = behaviour;
  const next: PropBehaviour = { ...rest, frames: { ...otherFrames, raised, raisedEmpty }, ...(Object.keys(animations).length === 0 ? {} : { animations }) };
  const before = frameWidth(placed);
  const after = frameWidth(lowered);
  const x = before === undefined || after === undefined ? prop.x : prop.x + (before - after) / (2 * tileSize);
  return { prop: { ...prop, frame: lowered, x, behaviour: next }, report: { propId: prop.id, lowered, raised, raisedEmpty, animations: Object.keys(named) } };
}

/**
 * Carries every two-state well of a world over to three states.
 *
 * @param world The document.
 * @param strips The world's one-shot strips by the frame id of their first frame, as the compile context holds
 *   them.
 * @param frameWidth Frame width in pixels, undefined when the frame has no known size; a well whose two frames
 *   differ in width keeps its feet point only where both are known.
 * @returns The document, the same object when no well was carried, and one report per carried well in
 *   document order.
 */
export function wellStates(world: WorldDocument, strips: Readonly<Record<string, TransitionStrip>>, frameWidth: (frame: TileRef) => number | undefined): { readonly world: WorldDocument; readonly wells: readonly WellReport[] } {
  const wells: WellReport[] = [];
  const maps = world.maps.map((map) => {
    let changed = false;
    const layers = map.layers.map((layer) => {
      if (layer.kind !== "prop") {
        return layer;
      }
      let touched = false;
      const props = layer.props.map((prop) => {
        const result = carried(prop, strips, frameWidth, world.tileSize);
        if (result.report !== undefined) {
          touched = true;
          wells.push({ mapId: map.id, layerId: layer.id, ...result.report });
        }
        return result.prop;
      });
      changed = changed || touched;
      return touched ? { ...layer, props } : layer;
    });
    return changed ? { ...map, layers } : map;
  });
  return wells.length === 0 ? { world, wells } : { world: { ...world, maps }, wells };
}

/**
 * One line for a carried well, naming the frame each state rests on so an author can check it on the map.
 *
 * @param report The report.
 * @returns The line.
 */
export function describeWell(report: WellReport): string {
  const frame = (ref: TileRef): string => `${ref.tileset} ${ref.tile}`;
  const plays = report.animations.length === 0 ? "plays no strip, so it cuts; choose strips in its Behaviour section" : `plays ${report.animations.join(" and ")}`;
  return `well ${report.propId} (map ${report.mapId}, layer ${report.layerId}): lowered on ${frame(report.lowered)}, raised on ${frame(report.raised)}, raised empty on ${frame(report.raisedEmpty)}; ${plays}`;
}

/**
 * Upgrades a document's wells from what a compile context holds (ADR-042's amendment of 2026-10-03, item 6): the
 * one-shot strips, and each frame's width from its size or, in a context written before sizes, its footprint.
 * A reader applies it to the document it parsed, in memory, through `upgradeWorld`; nothing is written.
 *
 * @param world The document.
 * @param context The world's compile context.
 * @returns The document, the same object when nothing was upgraded, and one report per upgraded well.
 */
export function upgradeWells(world: WorldDocument, context: Pick<CompileContext, "transitionStrips" | "frameSizes" | "collision">): { readonly world: WorldDocument; readonly wells: readonly WellReport[] } {
  const sizes = context.frameSizes ?? {};
  const footprints = context.collision.footprints ?? {};
  return wellStates(world, context.transitionStrips ?? {}, (frame) => {
    const id = frameId(frame.tileset, null, frame.tile);
    return sizes[id]?.w ?? footprints[id]?.w;
  });
}

/**
 * The editor's notes on the wells a map's document was upgraded with as it opened: a warning on each prop,
 * saying what changed and that a save keeps it. A well no longer on the map is not noted.
 *
 * @param wells The reports of the upgrade.
 * @param map The map in view.
 * @returns The warnings, in report order.
 */
export function upgradeNotes(wells: readonly WellReport[], map: MapDocument): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  for (const well of wells) {
    const layer = well.mapId === map.id ? map.layers.find((l) => l.id === well.layerId) : undefined;
    if (layer?.kind !== "prop" || !layer.props.some((prop) => prop.id === well.propId)) {
      continue;
    }
    const plays = well.animations.length === 0 ? "plays no strip, so it cuts" : `plays its ${well.animations.join(" and ")} strips`;
    issues.push({ mapId: well.mapId, kind: "well.upgraded", layerId: well.layerId, propId: well.propId, severity: "warning", message: `prop ${well.propId} is a Well saved with two states, upgraded as it was opened: it now starts lowered and ${plays}; save to keep it` });
  }
  return issues;
}
